// NOTICE: This file was copied from the oppizi-shared repository, do
// not modify it here.

/* eslint-disable no-console */
/**
 * @fileOverview
 * - Provides a set of functions that client code can use to connect their platform-specific
 *   WebSocket implementation and other config (i.e. stage, API key...) to.
 * - Provides `use`, `release` and `sendData` functions for client code to interact with websockets.
 * - Contains runtime data and functions for maintenance/handling of the websocket connection.
 * - Defines a websocket Redux reducer that can be connected to the platform/mobile app store.
 *   It writes entirely to its own state object, leaving the rest of the application state
 *   untouched.
 * - Does NOT provide any callbacks for when websocket events occur. The reducer handles all state
 *   changes such that client code only needs to consume the Redux state - not caring about
 *   websocket events themselves.
 *
 * Note that the terminology "users", "numUsers", etc., when seen in this file is different to
 * the rest of the codebase. That is, it does not refer to database Users (admins/brand ambassadors,
 * etc.), but parts of the codebase that intend to "use" websockets.
 *
 * As an example, if there existed a live map page and chat in the side-bar of the platform, then
 * those two parts of the codebase would be two different users. The side-bar will call `use` to
 * register its intent to use websockets, and so would the live map page. Then, when the real user
 * navigates off of the live map page, that part of the code base would `release` its usage of
 * websockets - but the websocket connection will be maintained because the side-bar still uses it.
 */

import { WEBSOCKETS_URLS } from '../constants'

import wsStateReducer from './ws-state-reducer'
import chatReducer from './chat-reducer'
import configureMissingDataFetcher from './missing-data-fetcher'
import { InvalidRequest } from '../error'

const RECONNECT_INTERVAL_MS = 10000
const CHAT_RESPONSE_TIMEOUT = 8000

const incomingMessageTypeWhitelist = [
  'CHAT_CONVERSATION',
  'CHAT_CONVERSATIONS',
  'CHAT_MESSAGE',
  'CHAT_USER',
]

// These will be set once `configure` is executed
let configured = false
let WebSocket
let store
let stage

// This will be set once `setAPIKey` is executed
let connectionUrl = undefined

let numUsers = 0
let activeWs = undefined
let connected = false
let connectionAttemptTimestamp = null

const initState = {
  connected: false,
  conversations: {},
  orderedConversations: [],
  validConversationsIds: [],
  messages: {},
  users: {},
  conversationsByMissionIds: {},
}

// This can technically grow to infinity, but in reality it'll probably never become an issue -
// even if we had one million users whose data requests failed, it still wouldn't be a problem.
const pendingAcks = {}

// -------- EXPORTS -------------------------------------------------

/**
 * This function should be called once when the client is being initialised,
 * before using any other websocket functions.
 *
 * @param {object} ws WebSocket implementation for the platform
 * @param {object} _store Redux store for this library to maintain
 * @param {string} _stage 'uat', 'staging' or 'prod'
 */
const configure = (ws, _store, _stage) => {
  if (configured) throw new InvalidRequest('Cannot configure more than once')
  configured = true

  WebSocket = ws
  store = _store
  stage = _stage

  setInterval(reconnect, RECONNECT_INTERVAL_MS)

  configureMissingDataFetcher(store, sendData)
}

/**
 * Configure the current user's API key with this. Safe to be re-invoked
 * if the user is switched, but `clearAPIKey` should ideally be called
 * at the moment the user logs out.
 *
 * Should be executed after `configure`.
 */
const setAPIKey = apiKey => {
  if (!configured) throw new InvalidRequest('Must call `configure` first')

  const safeAPIKey = encodeURIComponent(apiKey)

  connectionUrl = `${WEBSOCKETS_URLS[stage]}?apiKey=${safeAPIKey}`
}

/**
 * Execute this when the user logs out (but after all websocket users have called
 * `release`) to prevent accidental re-connection using the wrong user.
 */
const clearAPIKey = () => {
  if (numUsers)
    throw new InvalidRequest('Must `release` all websocket connections first')

  connectionUrl = undefined
}

/**
 * Clients express their intent to use websockets by invoking this.
 * Supports being executed more than once (i.e. chat in side-bar and live map).
 */
const use = handlers => {
  if (numUsers++ === 0) {
    if (!configured || !connectionUrl)
      throw new InvalidRequest('Must call `configure` and `setAPIKey` first')

    connect(handlers)
  }
}

/**
 * Is the reverse of `use`. If all uses are released then the websocket
 * is disconnected.
 */
const release = () => {
  if (numUsers === 0) {
    console.warn('There are no users to release')
  }

  if (--numUsers === 0) disconnect()
}

/**
 * @param {object} data Will be `JSON.stringify`-ed and then sent through
 *                      the websocket.
 * @return {null|Promise} Returns a promise if `data` has a truthy `ack`.
 */
const sendData = data => {
  try {
    if (typeof data !== 'object')
      throw new InvalidRequest('Data must be an object')
    if (typeof data.action !== 'string')
      throw new InvalidRequest('Data must contain a string `action`')
    if (!connected)
      throw new InvalidRequest('Can only send messages while connected')
    if (data.ack && data.ack in pendingAcks)
      throw new InvalidRequest('`ack` already exists')

    activeWs.send(JSON.stringify(data))

    if (data.dispatchPayload) {
      store.dispatch(data.dispatchPayload)
    }
    if (!data.ack) return
    const promise = new Promise((resolve, reject) => {
      pendingAcks[data.ack] = resolve
      setTimeout(() => {
        reject()
        delete pendingAcks[data.ack]
      }, CHAT_RESPONSE_TIMEOUT)
    })
    return promise
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('LEGACY_WS_ERROR', error)
  }
}

const reducer = (state = initState, action) =>
  reducerChain.reduce((newState, reducer) => reducer(newState, action), state)

export { configure, setAPIKey, clearAPIKey, use, release, sendData, reducer }

// -------- __NOT__ EXPORTED ----------------------------------------

const connect = (handlers = {}) => {
  if (shouldTryToConnect()) {
    saveConnectionAttemptTimestamp()

    const { onConnect = null } = handlers

    activeWs = new WebSocket(connectionUrl)

    activeWs.addEventListener('open', () => connOpen(onConnect))
    activeWs.addEventListener('close', connClosed)
    activeWs.addEventListener('message', messageReceived)
    activeWs.addEventListener('error', connError)
  }
}

const disconnect = () => {
  if (activeWs) {
    activeWs.removeEventListener('open', connOpen)
    activeWs.removeEventListener('close', connClosed)
    activeWs.removeEventListener('message', messageReceived)
    activeWs.removeEventListener('error', connError)

    activeWs.close()
  }
}

const connOpen = onConnect => {
  try {
    connected = true

    store.dispatch({ 'type': 'WEBSOCKETS_CONNECT' })
    if (typeof onConnect === 'function') {
      onConnect()
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('LEGACY_WS_ERROR', error)
  }
}

const connClosed = () => {
  try {
    connected = false

    store.dispatch({ 'type': 'WEBSOCKETS_DISCONNECT' })

    if (numUsers !== 0) connect()
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('LEGACY_WS_ERROR', error)
  }
}

const connError = () => {
  for (const ack in pendingAcks) {
    if (pendingAcks.hasOwnProperty(ack)) {
      const pendingAckResolve = pendingAcks[ack]
      pendingAckResolve()
    }
  }
}

const messageReceived = data => {
  try {
    let content
    try {
      content = JSON.parse(data.data)
    } catch (e) {
      if (e instanceof SyntaxError) return
      throw e
    }

    if (!Array.isArray(content)) {
      throw new InvalidRequest(
        `Websocket messages must be an array of objects. Received: ${JSON.stringify(
          content,
        )}`,
      )
    }

    for (const obj of content) {
      if (typeof obj !== 'object') {
        throw new InvalidRequest(
          `Websocket messages must be an array of objects. Received: ${JSON.stringify(
            content,
          )}`,
        )
      }

      if (obj.type === 'ADVICE_RECONNECT') {
        disconnect()
        continue
      }

      if (obj.type === 'ACK') {
        checkAck(obj.ack)
        continue
      }

      if (incomingMessageTypeWhitelist.indexOf(obj.type) === -1) {
        // Warning instead of throwing for backwards compatibility
        // eslint-disable-next-line no-console
        console.warn('Ignoring unknown websocket message type: ' + obj.type)
        return
      }

      store.dispatch(obj)

      if (obj.ack) checkAck(obj.ack, obj)
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('LEGACY_WS_ERROR', error)
  }
}

const reconnect = () => {
  if (numUsers !== 0 && !connected) connect()
}

const checkAck = (ack, data) => {
  if (!(ack in pendingAcks)) return
  pendingAcks[ack](data || ack)
  delete pendingAcks[ack]
}

const shouldTryToConnect = () => {
  if (!connectionAttemptTimestamp) {
    return true
  }
  return Date.now() - connectionAttemptTimestamp > RECONNECT_INTERVAL_MS
}

const saveConnectionAttemptTimestamp = () => {
  connectionAttemptTimestamp = Date.now()
}

const reducerChain = [wsStateReducer, chatReducer]
